import { useMemo, useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { useTranslation } from '../../../lib/i18n';
import { messageDir } from '../../../lib/message-direction';
import { Button, ScrollArea } from '../ui';
import styles from './DocViewerModal.module.css';

// A plain-text document — `.txt`, `.log`, `.json`, `.yaml`/`.yml`. Before this they were `kind === null` and
// handed to the browser, where WixMP's `Content-Disposition` decided whether you got a preview or a download.
//
// NOT built on `CodeBlockCard` (`components/Markdown.tsx`), deliberately, even though it looks like the same
// thing. Three reasons, each of which would have cost more than the ~15 lines here:
//   - its Download saves `snippet.<ext>` from the fence language, which is right for a chat snippet and wrong
//     for a real file that has a name (the viewer footer's Download already handles this correctly);
//   - its two controls are hand-rolled button elements — the whole of `components/Markdown.tsx: 2` on
//     `button-spec`'s PENDING_MIGRATION list — so reusing them would spread a legacy look onto a new surface
//     instead of shrinking that list. (Spelling the tag out here would trip that same guard, which scans raw
//     source rather than code-only — the trap `test/doc-viewer-sandbox.test.ts` documents for itself.)
//   - its root is inline-styled with no height/flex hook, so it cannot be constrained to the viewer body
//     without adding a `className` prop to a component every chat message renders.
//
// NO SYNTAX HIGHLIGHTING, and that is a scope decision rather than an oversight: there is no highlighter in
// this package, and adding one means a large dependency plus 10+ token colours the design system does not
// define — a token-layer change wearing a component-layer disguise. Monospace + wrapping is phase 1.
export function TextDocView({ text, fileName }: { text: string; fileName: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  // A `.json` file is stored minified as often as not, and one 40 KB line is not a preview. Claiming to
  // render JSON and then showing an unreadable wall would be a half-answer, so pretty-print it — and fall
  // back to the raw text when it does not parse, since a broken JSON file is exactly when you want to look.
  const body = useMemo(() => (isJson(fileName) ? prettyJson(text) : text), [text, fileName]);
  const dir = messageDir(body);

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(body);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    } catch {
      // Clipboard is gated on focus and permissions; a refusal is not worth an error state.
    }
  };

  return (
    <div className={styles.textBody}>
      <div className={styles.textActions}>
        <Button
          variant="outlined"
          size="tiny"
          startIcon={copied ? <Check size={16} aria-hidden /> : <Copy size={16} aria-hidden />}
          onClick={() => void onCopy()}
        >
          {copied ? t('markdown.copied') : t('markdown.copy')}
        </Button>
      </div>
      {/* `component="pre"` keeps the monospace semantics while the scrolling stays the designed bar — never a
          raw `overflow` (see CLAUDE.md "Scrollbars"). */}
      <ScrollArea component="pre" className={styles.textPre} dir={dir}>
        {body}
      </ScrollArea>
    </div>
  );
}

const isJson = (fileName: string): boolean => fileName.trim().toLowerCase().endsWith('.json');

function prettyJson(text: string): string {
  try {
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch {
    return text;
  }
}
